import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledForm from 'Components/StyledForm'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import { SELECTED_TICKET_SALE_TARGET_PARAMETER_KEY } from 'Services/SalesManagement/Features/TicketSale/Hooks/useTicketSaleTargetsWithParams'
import {
  useCreateSingleTicketSaleTargetMutation,
  useUpdateSingleTicketSaleTargetMutation
} from 'Services/SalesManagement/Features/TicketSale/ticketSaleApi'
import type { TicketSaleTarget } from 'Services/SalesManagement/Features/TicketSale/types'
import type { FieldValidationErrors } from 'Services/SalesManagement/types'
import useTypedSelector from 'Store'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: FieldValidationErrors = {
  identifier: [],
  description: [],
  authority: []
}

interface TicketSaleTargetFormProps {
  successBaseUrl: string
  ticketSaleTarget?: TicketSaleTarget
}

export default function TicketSaleTargetForm({
  successBaseUrl,
  ticketSaleTarget
}: Readonly<TicketSaleTargetFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!ticketSaleTarget

  const [createSingleTicketSaleTarget, createStatus] = useCreateSingleTicketSaleTargetMutation()
  const [updateSingleTicketSaleTarget, updateStatus] = useUpdateSingleTicketSaleTargetMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const [ticketSaleTargetDetails, setTicketSaleTargetDetails] = useState<Partial<TicketSaleTarget>>(
    (isEdit && ticketSaleTarget) || {}
  )
  const { user } = useTypedSelector(state => state.user)
  const { authorities } = useAuthorityContext()

  const [fieldErrors, setFieldErrors] = useState<FieldValidationErrors>(fieldErrorsDefaultState)

  const handleChange = (value: string, field: keyof TicketSaleTarget) => {
    setTicketSaleTargetDetails(prevState => ({ ...prevState, [field]: value }))
    setFieldErrors(prevState => ({ ...prevState, [field]: [] }))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setFieldErrors(fieldErrorsDefaultState)
    const ticketSaleTargetData = {
      ...ticketSaleTargetDetails,
      authority: isSuperuser(user) ? ticketSaleTargetDetails.authority : user?.pta
    }

    const mutation = isEdit
      ? updateSingleTicketSaleTarget({
          ...ticketSaleTargetData,
          id: ticketSaleTarget.id
        })
      : createSingleTicketSaleTarget(ticketSaleTargetData)

    mutation
      .unwrap()
      .then(result => {
        SnackbarUtils.success(t`Ticket sale target successfully saved.`)
        if (isEdit) {
          navigate(successBaseUrl)
        } else {
          navigate(
            appendQueryParamsToUrl(successBaseUrl, {
              [SELECTED_TICKET_SALE_TARGET_PARAMETER_KEY]: result.id
            })
          )
        }
      })
      .catch(err => {
        if (err && 'data' in err) {
          const validationErrors = err.data as FieldValidationErrors
          if (validationErrors) {
            setFieldErrors(prevState => ({
              ...prevState,
              ...validationErrors
            }))
          } else {
            SnackbarUtils.error(t`There was an error saving the ticket sale target.`)
          }
        }
      })
  }

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
          <Box>
            <TextField
              name="identifier"
              label={t`Identifier`}
              type="text"
              required
              slotProps={{
                htmlInput: { maxLength: 25 }
              }}
              value={ticketSaleTargetDetails?.identifier}
              error={fieldErrors.identifier.length > 0}
              onChange={e => handleChange(e.target.value, 'identifier')}
              helperText={fieldErrors.identifier.length > 0 ? fieldErrors.identifier[0] : ''}
            />
          </Box>
          <Box>
            <TextField
              name="desctiption"
              label={t`Description`}
              size="small"
              sx={{ minWidth: 400 }}
              value={ticketSaleTargetDetails?.description}
              error={fieldErrors.description.length > 0}
              onChange={e => handleChange(e.target.value, 'description')}
              helperText={fieldErrors.description.length > 0 ? fieldErrors.description[0] : ''}
              multiline={true}
              rows={3}
            />
          </Box>
          {isSuperuser(user) && (
            <Box>
              <FormControl sx={{ minWidth: 200 }} required>
                <InputLabel>{t`Authority`}</InputLabel>
                <Select
                  name="authority"
                  error={fieldErrors.authority.length > 0}
                  required
                  label={t`Authority`}
                  value={ticketSaleTargetDetails.authority ?? ''}
                  onChange={e => handleChange(e.target.value, 'authority')}
                >
                  {authorities?.map(({ code, name }) => (
                    <MenuItem key={code} value={code}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={fieldErrors.authority.length > 0}>
                  {fieldErrors.authority.length > 0 ? fieldErrors.authority[0] : ''}
                </FormHelperText>
              </FormControl>
            </Box>
          )}
          <CustomLoadingButton
            isLoading={isSubmitLoading}
            type="submit"
            size="large"
            iconPosition="end"
          >
            {t`Save ticket sale target`}
          </CustomLoadingButton>
        </StyledForm>
      </Grid>
    </Grid>
  )
}
